import { useState, useEffect, useRef, useCallback } from "react";
import PropTypes from "prop-types";

/**
 * Paranneltu kuvagalleria:
 * - Tukee images: string[] TAI {src, alt?, title?, w?, h?}[]
 * - Automaattinen toisto (Play/Pause)
 * - Nuolinäppäimet (← →) ja Esc lightboxille
 * - Pikkukuvakaruselli, aktiivinen korostuu
 * - Lightbox (kokonäyttö)
 * - Lazy loading + decoding="async"
 * - CLS-minimointi: width/height kun tiedossa, muuten aspect-ratio varalla
 */
const BandGallery = ({
  images = [],
  bandName,
  autoPlay = true,
  intervalMs = 3500,
  loop = true,
  showLightbox = true,
  defaultRatio = 16 / 9,     // käytetään, jos w/h ei ole tiedossa
  sizes = "(min-width: 1024px) 900px, 100vw", // <img sizes> – voit säätää asettelun mukaan
}) => {
  const [index, setIndex] = useState(0);
  const [playing, setPlaying] = useState(autoPlay);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [fadeKey, setFadeKey] = useState(0); // pakottaa CSS-animoinnin
  const timerRef = useRef(null);

  // --- Normalisoi sisään tulevat kuvat yhdenmukaiseen muotoon ---
  const norm = useCallback(
    (it, i) => {
      if (!it) return null;
      if (typeof it === "string") {
        const src = it.trim();
        if (!src) return null;
        return {
          src,
          alt: `${bandName} galleria kuva #${i + 1}`,
          title: `${bandName} – galleria #${i + 1}`,
          w: undefined,
          h: undefined,
        };
      }
      const src = (it.src || it.url || "").trim();
      if (!src) return null;
      return {
        src,
        alt: it.alt || `${bandName} galleria kuva #${i + 1}`,
        title: it.title || `${bandName} – galleria #${i + 1}`,
        w: it.w || it.width,
        h: it.h || it.height,
      };
    },
    [bandName]
  );

  const items = images.map(norm).filter(Boolean);

  // Varmista alkukuva
  useEffect(() => {
    if (items.length > 0) setIndex(0);
  }, [items.length]);

  const go = useCallback(
    (next) => {
      if (items.length === 0) return;
      const len = items.length;
      let newIndex = index + next;
      if (loop) {
        newIndex = (newIndex + len) % len;
      } else {
        newIndex = Math.max(0, Math.min(len - 1, newIndex));
      }
      setIndex(newIndex);
      setFadeKey((k) => k + 1);
    },
    [index, items.length, loop]
  );

  const goTo = useCallback(
    (i) => {
      if (i === index) return;
      setIndex(i);
      setFadeKey((k) => k + 1);
    },
    [index]
  );

  // Autoplay
  useEffect(() => {
    if (!playing || items.length <= 1) return;
    timerRef.current = setInterval(() => go(1), intervalMs);
    return () => clearInterval(timerRef.current);
  }, [playing, go, intervalMs, items.length]);

  // Näppäimistö-ohjaus
  useEffect(() => {
    const onKey = (e) => {
      if (isLightboxOpen) {
        if (e.key === "Escape") setIsLightboxOpen(false);
        if (e.key === "ArrowRight") go(1);
        if (e.key === "ArrowLeft") go(-1);
        return;
      }
      if (e.key === "ArrowRight") go(1);
      if (e.key === "ArrowLeft") go(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [go, isLightboxOpen]);

  if (!items || items.length === 0) {
    return (
      <p className="text-center text-lg font-semibold">
        Kuvia ei ole saatavilla.
      </p>
    );
  }

  const activeImg = items[index];
  const hasSize = Number(activeImg.w) > 0 && Number(activeImg.h) > 0;
  const ratio = hasSize ? activeImg.h / activeImg.w : defaultRatio;

  return (
    <div className="max-w-6xl mx-auto px-4 flex flex-col items-center">
      <h2
        className="
          font-customFont2 dark:text-shadow-lg
          text-center text-darkest dark:text-primary text-black
          text-3xl sm:text-3xl md:text-3xl lg:text-3xl xl:text-4xl 2xl:text-4xl
          font-bold leading-tight mb-4
        "
      >
        Kuvagalleria
      </h2>

      {/* Pääkuva + kontrollit */}
      <div className="w-full max-w-[900px] mx-auto mb-6 relative">
        {/* Kehys varaa tilan: width/height -> selain laskee tilan; muuten aspect-ratio */}
        <button
          type="button"
          aria-label="Avaa kuva isompana"
          onClick={() => showLightbox && setIsLightboxOpen(true)}
          className="block w-full focus:outline-none group"
          title={activeImg.title}
          style={hasSize ? undefined : { aspectRatio: `${1 / (1 / ratio)}` }} // ts. ratio
        >
          <img
            key={fadeKey}
            src={activeImg.src}
            alt={activeImg.alt}
            title={activeImg.title}
            loading="lazy"
            decoding="async"
            {...(hasSize ? { width: activeImg.w, height: activeImg.h } : {})}
            className="
              w-full h-auto max-h-[600px] object-contain rounded-lg
              transition-opacity duration-500 ease-out opacity-0
              [animation:fadein_0.5s_ease-out_forwards]
            "
            sizes={sizes}
          />
        </button>

        {/* Edellinen/Seuraava -nuolet */}
        {items.length > 1 && (
          <>
            <button
              aria-label="Edellinen kuva"
              onClick={() => go(-1)}
              className="
                absolute left-2 top-1/2 -translate-y-1/2
                p-2 rounded-full bg-black/40 hover:bg-black/60 text-white
                shadow-md focus:outline-none focus:ring-2 focus:ring-white
              "
            >
              ‹
            </button>
            <button
              aria-label="Seuraava kuva"
              onClick={() => go(1)}
              className="
                absolute right-2 top-1/2 -translate-y-1/2
                p-2 rounded-full bg-black/40 hover:bg-black/60 text-white
                shadow-md focus:outline-none focus:ring-2 focus:ring-white
              "
            >
              ›
            </button>
          </>
        )}

        {/* Pelinapit */}
        {items.length > 1 && (
          <div className="absolute bottom-3 right-3 flex items-center gap-2">
            <button
              onClick={() => setPlaying((p) => !p)}
              className="
                px-3 py-1 rounded-full bg-black/40 hover:bg-black/60 text-white
                text-sm shadow-md focus:outline-none focus:ring-2 focus:ring-white
              "
              aria-label={playing ? "Pysäytä automaattinen toisto" : "Käynnistä automaattinen toisto"}
              title={playing ? "Pysäytä" : "Play"}
            >
              {playing ? "Tauko" : "Jatka"}
            </button>
            <span
              className="px-2 py-1 rounded-full bg-black/30 text-white text-xs"
              aria-live="polite"
            >
              {index + 1} / {items.length}
            </span>
          </div>
        )}
      </div>

      {/* Pikkukuvat */}
      <div className="flex flex-wrap justify-center gap-3">
        {items.map((img, idx) => {
          const active = idx === index;
          const thumbHasSize = Number(img.w) > 0 && Number(img.h) > 0;
          const thumbRatio = thumbHasSize ? img.h / img.w : defaultRatio;
          return (
            <button
              key={img.src + "_" + idx}
              onClick={() => goTo(idx)}
              aria-label={`Näytä kuva ${idx + 1}`}
              className={`
                relative w-20 h-20 sm:w-24 sm:h-24 overflow-hidden rounded-md
                shadow-md focus:outline-none
                ${active ? "ring-2 ring-primary ring-offset-2 ring-offset-white dark:ring-offset-night-800" : "ring-0"}
              `}
              title={img.title}
              style={thumbHasSize ? undefined : { aspectRatio: `${1 / (1 / thumbRatio)}` }}
            >
              <img
                src={img.src}
                alt={`${bandName} galleria kuva #${idx + 1}`}
                loading="lazy"
                decoding="async"
                {...(thumbHasSize ? { width: img.w, height: img.h } : {})}
                className={`
                  w-full h-full object-cover transition-transform duration-300
                  ${active ? "scale-105" : "hover:scale-105"}
                `}
                sizes="96px"
              />
              {active && (
                <span className="absolute inset-0 border-2 border-primary rounded-md pointer-events-none" />
              )}
            </button>
          );
        })}
      </div>

      {/* Lightbox */}
      {showLightbox && isLightboxOpen && (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setIsLightboxOpen(false)}
        >
          <div
            className="relative max-w-5xl w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={activeImg.src}
              alt={`${bandName} suurennettu kuva ${index + 1}/${items.length}`}
              title={activeImg.title}
              className="w-full h-auto max-h-[85vh] object-contain rounded-lg"
              loading="eager"
              decoding="async"
              {...(hasSize ? { width: activeImg.w, height: activeImg.h } : {})}
            />

            {/* Close */}
            <button
              aria-label="Sulje"
              onClick={() => setIsLightboxOpen(false)}
              className="absolute top-3 right-3 p-2 rounded-full bg-black/60 text-white hover:bg-black/80 focus:outline-none focus:ring-2 focus:ring-white"
              title="Sulje"
            >
              ✕
            </button>

            {/* Nav lightboxissa */}
            {items.length > 1 && (
              <>
                <button
                  aria-label="Edellinen kuva"
                  onClick={() => go(-1)}
                  className="absolute left-3 top-1/2 -translate-y-1/2 p-3 rounded-full bg-black/60 text-white hover:bg-black/80 focus:outline-none focus:ring-2 focus:ring-white"
                  title="Edellinen"
                >
                  ‹
                </button>
                <button
                  aria-label="Seuraava kuva"
                  onClick={() => go(1)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-3 rounded-full bg-black/60 text-white hover:bg-black/80 focus:outline-none focus:ring-2 focus:ring-white"
                  title="Seuraava"
                >
                  ›
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Fade animaatio */}
      <style>{`
        @keyframes fadein {
          from { opacity: 0 }
          to   { opacity: 1 }
        }
      `}</style>
    </div>
  );
};

BandGallery.propTypes = {
  // nyt sallitaan string TAI objekti, jossa voi olla mitat
  images: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        src: PropTypes.string.isRequired,
        alt: PropTypes.string,
        title: PropTypes.string,
        w: PropTypes.number,
        h: PropTypes.number,
      }),
    ])
  ).isRequired,
  bandName: PropTypes.string.isRequired,
  autoPlay: PropTypes.bool,
  intervalMs: PropTypes.number,
  loop: PropTypes.bool,
  showLightbox: PropTypes.bool,
  defaultRatio: PropTypes.number,
  sizes: PropTypes.string,
};

export default BandGallery;
